<div class="flex items-center justify-between mb-4">
  <h2 class="text-xl font-semibold">Lyfis Scorecard Compilations</h2>
  <button
    nz-button
    nzType="default"
    (click)="loadScorecards()"
    [nzLoading]="loading"
  >
    Refresh
  </button>
</div>

<nz-table
  [nzData]="groupedScorecards"
  [nzLoading]="loading"
  [nzFrontPagination]="false"
  [nzShowPagination]="false"
  class="mb-4"
>
  <thead>
    <tr>
      <th>Rules Version</th>
      <th>File Name</th>
      <th>Disease</th>
      <th>Compile Date</th>
      <th>Upload Status</th>
      <th>Compile Status</th>
      <th>Actions</th>
    </tr>
  </thead>
  <tbody>
    <ng-container *ngFor="let group of groupedScorecards">
      <!-- Group Header Row -->
      <tr class="bg-gray-100 font-semibold">
        <td colspan="7" class="p-2">
          <div class="flex items-center justify-between">
            <div>
              <span class="font-mono text-xs">{{ group.rulesVersionId }}</span>
              <span class="text-gray-600 text-sm ml-2">
                ({{ group.totalCount }} scorecard{{
                  group.totalCount > 1 ? "s" : ""
                }})
              </span>
            </div>
            <div class="flex gap-2 text-xs">
              <span *ngIf="group.hasSuccessful" class="text-green-600"
                >✓ Has Successful</span
              >
              <span *ngIf="group.hasRejected" class="text-red-600"
                >✗ Has Rejected</span
              >
            </div>
          </div>
        </td>
      </tr>
      <!-- Scorecard Rows in this Group -->
      <tr *ngFor="let row of group.scorecards" class="hover:bg-gray-50">
        <td></td>
        <!-- Empty cell under Rules Version (already shown in header) -->
        <td>{{ row.fileName }}</td>
        <td>{{ row.diseaseName || row.diseaseId }}</td>
        <td>{{ row.compiledAt | date: "short" }}</td>
        <td>{{ row.uploadStatus }}</td>
        <td>{{ row.compileStatus }}</td>
        <td class="actions">
          <button nz-button nzType="link" (click)="viewCompilation(row)">
            View
          </button>
        </td>
      </tr>
    </ng-container>
  </tbody>
</nz-table>

<nz-drawer
  [nzVisible]="drawerVisible"
  nzTitle="Compilation Details"
  (nzOnClose)="closeDrawer()"
  [nzWidth]="900"
>
  <ng-container *nzDrawerContent>
    <ng-container *ngIf="selected">
      <div class="font-mono break-all text-sm mb-2">
        Scorecard ID: {{ selected.scorecard.scorecardId || "N/A" }} · Disease:
        {{ selected.scorecard.diseaseId || "N/A" }}
      </div>
      <p class="text-gray-600 text-sm mb-3">
        Rules Version:
        {{
          selected.compiledArtifact?.rulesVersionId ||
            selected.scorecard.rulesVersionId ||
            "-"
        }}
      </p>

      <!-- Status Section -->
      <div
        class="mb-4 p-3 rounded-md"
        [ngClass]="{
          'bg-green-50 border border-green-200': selected.compiledArtifact,
          'bg-red-50 border border-red-200':
            !selected.compiledArtifact && latestRejectedErrors.length > 0,
          'bg-yellow-50 border border-yellow-200':
            !selected.compiledArtifact && latestRejectedErrors.length === 0
        }"
      >
        <div class="flex items-center justify-between">
          <div>
            <h4 class="font-medium mb-1">
              <span *ngIf="selected.compiledArtifact" class="text-green-700"
                >✓ Compilation Successful</span
              >
              <span
                *ngIf="
                  !selected.compiledArtifact && latestRejectedErrors.length > 0
                "
                class="text-red-700"
                >✗ Compilation Failed</span
              >
              <span
                *ngIf="
                  !selected.compiledArtifact &&
                  latestRejectedErrors.length === 0
                "
                class="text-yellow-700"
                >⏳ Compilation Status:
                {{ selected.scorecard.compileStatus || "Unknown" }}</span
              >
            </h4>
            <p class="text-sm text-gray-600">
              Compile Status:
              <strong>{{ selected.scorecard.compileStatus || "N/A" }}</strong>
              | Upload Status:
              <strong>{{ selected.scorecard.uploadStatus || "N/A" }}</strong>
            </p>
            <p
              class="text-xs text-gray-500 mt-1"
              *ngIf="selected.compileLogs?.length"
            >
              Found {{ selected.compileLogs.length }} compile log(s)
            </p>
          </div>
          <div class="flex gap-2">
            <button
              *ngIf="selected.compiledArtifact"
              nz-button
              nzType="default"
              nzSize="small"
              (click)="downloadConfig()"
            >
              Download config.json
            </button>
            <button
              *ngIf="selected.compiledArtifact"
              nz-button
              nzType="default"
              nzSize="small"
              (click)="downloadEngine()"
            >
              Download engine.js
            </button>
            <button
              nz-button
              nzType="primary"
              nzSize="small"
              (click)="downloadExcel()"
            >
              Download Excel
            </button>
          </div>
        </div>
      </div>

      <!-- Compilation Errors (if failed) -->
      <div
        class="mb-4"
        *ngIf="!selected.compiledArtifact && latestRejectedErrors.length > 0"
      >
        <h4 class="font-medium mb-2 text-red-700">
          Compilation Errors ({{ latestRejectedErrors.length }})
        </h4>
        <nz-table
          [nzData]="latestRejectedErrors"
          [nzFrontPagination]="false"
          [nzShowPagination]="false"
          [nzScroll]="{ x: '100%' }"
          class="text-sm"
        >
          <thead>
            <tr>
              <th nzWidth="100px">Code</th>
              <th nzWidth="80px">Sheet</th>
              <th nzWidth="60px">Row</th>
              <th nzWidth="200px">Current Value</th>
              <th>Fix Suggestion</th>
              <th nzWidth="200px">New Value</th>
              <th>Explanation</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let error of latestRejectedErrors">
              <td class="font-mono">{{ error.code || "-" }}</td>
              <td>{{ error.sheet || "-" }}</td>
              <td>{{ error.row || "-" }}</td>
              <td class="font-mono text-xs break-all">
                {{ error.currentValue || "-" }}
              </td>
              <td class="text-xs">{{ error.fix?.suggestion || "-" }}</td>
              <td class="font-mono text-xs break-all">
                {{ error.fix?.newValue || "-" }}
              </td>
              <td class="text-xs">
                {{ error.fix?.explanation || error.details || "-" }}
              </td>
            </tr>
          </tbody>
        </nz-table>
      </div>

      <!-- Compiled Artifacts (if successful) -->
      <div class="mb-4" *ngIf="selected.compiledArtifact">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <h4 class="font-medium mb-2">Config (JSON)</h4>
            <textarea
              readonly
              rows="12"
              class="w-full font-mono text-sm border border-gray-200 rounded-md p-2"
              >{{ selected.compiledArtifact.config | json }}</textarea
            >
          </div>
          <div>
            <h4 class="font-medium mb-2">Engine Code (JS)</h4>
            <textarea
              readonly
              rows="12"
              class="w-full font-mono text-sm border border-gray-200 rounded-md p-2"
              >{{ selected.compiledArtifact.engineCode }}</textarea
            >
          </div>
        </div>
      </div>

      <!-- Compile Logs -->
      <div class="mt-4">
        <h4 class="font-medium mb-2">Compile Logs</h4>
        <div *ngIf="groupedLogs?.length; else noLogs">
          <div
            *ngFor="let group of groupedLogs"
            class="mb-4 border border-gray-200 rounded-md p-3"
          >
            <div class="flex items-center justify-between mb-2">
              <div>
                <h5 class="font-medium text-sm">
                  Rule Version:
                  <span class="font-mono text-xs">{{
                    group.rulesVersionId
                  }}</span>
                </h5>
                <p class="text-xs text-gray-500">
                  {{ group.logs.length }} compilation attempt(s)
                  <span *ngIf="group.totalErrors > 0" class="text-red-600 ml-2">
                    · {{ group.totalErrors }} total error(s)
                  </span>
                </p>
              </div>
              <span
                *ngIf="group.hasRejected"
                class="text-xs text-red-600 font-medium"
              >
                Contains Rejected
              </span>
            </div>
            <ul class="list-disc pl-5 space-y-1">
              <li *ngFor="let log of group.logs" class="font-mono text-sm">
                <span>{{ log?.timestamp | date: "short" }}</span> ·
                <span
                  [ngClass]="{
                    'text-green-600': log?.status === 'ok',
                    'text-red-600': log?.status === 'rejected'
                  }"
                  >{{ log?.status || "unknown" }}</span
                >
                <span
                  *ngIf="log?.errorCount && log.errorCount > 0"
                  class="text-red-600 ml-2"
                >
                  ({{ log.errorCount }} error(s))
                </span>
              </li>
            </ul>
          </div>
        </div>
        <ng-template #noLogs>
          <p class="text-sm text-gray-500 italic">No compile logs found</p>
        </ng-template>
      </div>

      <!-- Compile Chat -->
      <div class="mt-4">
        <h4 class="font-medium mb-2">Compile Chat</h4>
        <div *ngIf="selectedChat?.length; else noChat">
          <ul class="space-y-2">
            <li
              *ngFor="let msg of selectedChat"
              class="border border-gray-100 rounded-md p-2"
            >
              <div class="flex items-center gap-2 text-sm">
                <strong class="capitalize">{{ msg.role }}</strong>
                <span class="font-mono text-gray-600">{{
                  msg.timestamp | date: "short"
                }}</span>
              </div>
              <div class="whitespace-pre-wrap text-sm mt-1">
                {{ msg.message }}
              </div>
            </li>
          </ul>
        </div>
        <ng-template #noChat>
          <p class="text-sm text-gray-500 italic">No chat messages found</p>
        </ng-template>
      </div>
    </ng-container>
    <div *ngIf="!selected" class="p-4 text-center text-gray-500">
      <p>No compilation details available</p>
    </div>
  </ng-container>
</nz-drawer>
